import { FC, useState, useCallback, useMemo } from 'react';
import Chip from '@/components/chip';
import DataTable from '@/components/data-table';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import Page, { PageProps } from '@/components/page';
import PageHeader from '@/components/page/header';
import Text from '@/components/text';
import { getEndUsers, getEndUserPolicyUsages } from '@/lib/services/user.service';
import { Box, Stack } from '@mui/material';
import NoneChip from '@/components/chip/none-chip';
import { GroupMeta } from '@/lib/models/group.model';
import Button from '@/components/button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';
import PageFeatureToast from '@/components/page-feature-toast';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import UserLink from '@/components/user-link';
import useDrawer from '@/hooks/use-drawer.hook';
import { useQuery } from 'react-query';
import { getAllGroups } from '@/lib/services/group.service';
import useQueryHelper from '@/hooks/use-query-helper';
import { cloneDeep } from 'lodash';
import { PolicyMeta } from '@/lib/models/policy-model-v1';

const columns: DtColumn[] = [
  { name: 'name', label: 'User', flex: 2 },
  { name: 'groups', label: 'Groups', flex: 2, sortable: false },
  { name: 'groupPolicy', label: 'Group Policies', flex: 2, sortable: false },
  { name: 'source', label: 'Source', flex: 0.8, sortable: false },
  { name: 'action', label: '', flex: 0.5, sortable: false },
];

const filter: DtFilter = {
  keyword: '',
  fields: [
    {
      columnName: 'groups',
      label: 'Group',
      control: 'select-multi',
      highlander: true,
      options: [],
    },
  ],
  values: [],
};

const UsersPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const { canChangeUsers, getTooltip } = useFeature();

  const query = useQuery(QueryKey.GroupsList, () => getAllGroups());
  const { data: groups } = query;
  const { showLoader: groupsLoading } = useQueryHelper(query);

  const compFilter = useMemo(() => {
    const groupFilterIndex = filter.fields.findIndex(({ columnName }) => columnName === 'groups');
    if (groupFilterIndex === -1) {
      return filter;
    }

    const updated = cloneDeep(filter);

    updated.fields[groupFilterIndex].options =
      groups?.map(({ id, name }) => ({ label: name, value: id })) || [];

    return updated;
  }, [groups]);

  const handleView = useCallback(
    (userId?: string) => {
      openDrawer('end-user', {
        id: userId || null,
        onClose: () => {
          setSelectedRowId('');
        },
      });

      setSelectedRowId(userId || '');
    },
    [openDrawer]
  );

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter) => {
      const { page, pageSize, sort } = pageParams;
      const [dataset, groupMeta] = await getEndUsers(page, pageSize, sort, filter);
      const users = dataset.rows;
      const userUsageMap = await getEndUserPolicyUsages(users);

      return {
        ...dataset,
        rows: users.map((user) => {
          const { id, groups: userGroupIds, sourceLabel, isSourceSystem } = user;
          const userGroups = userGroupIds
            .map((groupId) => groupMeta.get(groupId))
            .filter((group) => group !== undefined);
          const hasGroups = !!userGroups.length;

          const userUsage = userUsageMap.get(id);
          const policies = userUsage?.policies || [];
          const hasPolicies = policies.length > 0;

          return {
            id,
            name: <UserLink user={user} action="none" />,
            groups: (
              <Box maxWidth="100%">
                <Text dotdot noWrap size="small">
                  {hasGroups ? (
                    <Stack direction="row" gap={1}>
                      {userGroups.map(({ id, name }: GroupMeta) => (
                        <Chip key={id} label={name} size="small" />
                      ))}
                    </Stack>
                  ) : (
                    <NoneChip />
                  )}
                </Text>
              </Box>
            ),
            groupPolicy: (
              <Box maxWidth="100%">
                <Text dotdot noWrap size="small">
                  {hasPolicies ? (
                    <Stack direction="row" gap={1}>
                      {policies.map(({ id, name }: PolicyMeta) => (
                        <Chip key={id} label={name} size="small" />
                      ))}
                    </Stack>
                  ) : (
                    <NoneChip />
                  )}
                </Text>
              </Box>
            ),
            source: isSourceSystem ? <NoneChip system /> : <Text size="small">{sourceLabel}</Text>,
            action: <OpenDrawerButton onClick={() => handleView(id)} />,
          };
        }),
      };
    },
    [handleView]
  );

  if (groupsLoading) {
    return null;
  }

  return (
    <Page title="End Users">
      <PageHeader>
        <PageBlurb>View users and their assigned groups.</PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-user')} disabled={canChangeUsers}>
            <Button
              label="Add User"
              icon="plus"
              size="small"
              onClick={() => handleView()}
              disabled={!canChangeUsers}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="add-user" can={canChangeUsers} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.UsersDataset}
          searchPlaceholder="Search by name or email"
          columns={columns}
          onLoad={loadData}
          filter={compFilter}
          search
          poll={15000}
          sort={{ columnName: 'name', direction: 'asc' }}
          selectedRowId={selectedRowId}
          rowHeight={70}
        />
      </DomSize>
    </Page>
  );
};

export default UsersPage;
